import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  saveOnboardingFocus: (focus) => api.post('/users/onboarding-focus', { focus }),
  // Password management
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword, confirmPassword) => 
    api.post('/auth/reset-password', { token, new_password: newPassword, confirm_password: confirmPassword }),
  changePassword: (currentPassword, newPassword, confirmPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword }),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token/${token}`),
};

// Bot Runs API
export const botRunsAPI = {
  create: (data) => api.post('/bot-runs', data),
  getAll: () => api.get('/bot-runs'),
  getById: (id) => api.get(`/bot-runs/${id}`),
};

// Projects API
export const projectsAPI = {
  create: (data) => api.post('/projects', data),
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  duplicate: (id) => api.post(`/projects/${id}/duplicate`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getStats: () => api.get('/admin/stats'),
  getBotRuns: () => api.get('/admin/bot-runs'),
  getDemoLeads: () => api.get('/admin/demo-leads'),
  // Admin Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.post('/admin/settings', data),
  // Runway Status
  getRunwayStatus: () => api.get('/admin/runway/status'),
  // Daily Content Operations
  getQueueStatus: () => api.get('/daily-content/queue-status'),
  processQueueNow: () => api.post('/daily-content/process-queue'),
  generateDailyContent: (data = {}) => api.post('/daily-content/generate-daily', {
    brand: data.brand || 'paygbot',
    videos_count: data.videos_count || 2,
    stories_count: data.stories_count || 3,
    ...data
  }),
  getContentHistory: (limit = 10) => api.get(`/daily-content/history?limit=${limit}`),
  // Email Drip Campaigns
  getDripStats: () => api.get('/leads/drip/stats'),
  runDripCampaigns: () => api.post('/leads/drip/run'),
};

// Demo Leads API
export const demoLeadsAPI = {
  create: (data) => api.post('/demo-leads', data),
};

// Content Generation API
export const contentAPI = {
  generate: (data) => api.post('/content/generate', data),
  generateStream: (data) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/content/generate-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  },
  getTodayMission: () => api.get('/content/today-mission'),
  // Phase 2 — Asset-driven Today's Mission. The dashboard tries this
  // first; the v1 endpoint above is kept as a fallback.
  getTodayMissionV2: () => api.get('/todays-mission-v2'),
  markTodayMissionDoneV2: (assetId) => api.post(`/todays-mission-v2/${assetId}/done`),
  getRecent: () => api.get('/content/recent'),
  getById: (id) => api.get(`/content/${id}`),
  getMe: () => api.get('/auth/me'),
  cloneViral: (data) => api.post('/content/clone-viral', data),
  
  // Posting Streak API
  getPostingStreak: () => api.get('/posting-streak'),
  confirmPost: (contentId, planId) => api.post('/confirm-post', {
    content_id: contentId,
    plan_id: planId
  }),
};

// User/Onboarding API
export const userAPI = {
  saveOnboarding: (data) => api.post('/users/onboarding', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/users/profile', data),
  getProgress: () => api.get('/users/progress'),
};

// Monetization API  
export const monetizationAPI = {
  buildOffer: (data) => api.post('/monetization/build-offer', data),
  getSalesPage: (data) => api.post('/monetization/sales-page', data),
  getEmailCampaign: (data) => api.post('/monetization/email-campaign', data),
  getMonetizationPlan: (data) => api.post('/monetization/plan', data),
  getOffers: () => api.get('/monetization/offers'),
  getSalesPages: () => api.get('/monetization/sales-pages'),
  getEmailCampaigns: () => api.get('/monetization/email-campaigns'),
};

// Weekly Plan API
export const weeklyPlanAPI = {
  create: (data) => api.post('/weekly-plan', data),
  getCurrent: () => api.get('/weekly-plan/current'),
  getTodayContent: () => api.get('/today-content'),
  linkContent: (contentId) => api.post(`/weekly-plan/link-content?content_id=${contentId}`),
  updateDay: (planId, dayIndex, status, contentId) => 
    api.put(`/weekly-plan/${planId}/day/${dayIndex}?status=${status}${contentId ? `&content_id=${contentId}` : ''}`),
};

// Growth Engine API
export const growthAPI = {
  getViralIdeas: (data) => api.post('/growth/viral-ideas', data || {}),
  getContentSuggestions: () => api.get('/growth/content-suggestions'),
  getWeeklyReview: () => api.get('/growth/weekly-review'),
  getSuggestedAction: () => api.get('/growth/suggested-action'),
};

// Video Generation API
export const videoAPI = {
  generate: (contentId, scriptText = null) => api.post('/generate-video', { 
    content_id: contentId,
    script_text: scriptText 
  }),
  getStatus: (contentId) => api.get(`/video-status/${contentId}`),
  getUsage: () => api.get('/video-usage'),
  
  // Async Job Queue API
  createJob: (contentId, scriptText = null) => api.post('/video/jobs', {
    content_id: contentId,
    script_text: scriptText
  }),
  getJobStatus: (jobId) => api.get(`/video/jobs/${jobId}`),
  listJobs: (status = null, limit = 10) => {
    let url = `/video/jobs?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  getQueueStats: () => api.get('/video/queue/stats'),
  
  // Runway Video Generation API
  createVideo: (data) => api.post('/videos/create', data),
  getVideoStatus: (jobId) => api.get(`/videos/status/${jobId}`),
  // Phase 4.1 — Video Engine v2 founder-only entrypoint. Same response
  // shape as the legacy pair above so the existing poll loop in
  // GenerateContentModal works with a one-line endpoint swap.
  // Backend rejects with 403 (non-founder) or 409 (env flag off).
  createVideoV2: (data) => api.post('/videos/create-v2', data),
  getVideoStatusV2: (jobId) => api.get(`/videos/v2-status/${jobId}`),
  // Voiceover preview — calls ElevenLabs ONLY, never Runway. Unwraps .data
  // so callers can read .audio_url / .audio_duration / .script_hash / .cached
  // directly.
  previewVoiceover: (data) => api.post('/videos/voiceover-preview', data).then((r) => r.data),
  // Round 85 — Sentence-aware script rewrite. Replaces the front-end
  // word-slice that was deleting CTAs. Returns:
  //   { success, shortened_script, original_words, shortened_words, method }
  shortenScript: (data) => api.post('/videos/shorten-script', data).then((r) => r.data),
  // Duration / pricing preflight — pure quote endpoint. NO DB writes,
  // NO Runway, NO ElevenLabs. Callers read .tier / .allowed /
  // .upcharge_required / .price_display / .message.
  estimatePreflight: (data) => api.post('/videos/preflight/estimate', data).then((r) => r.data),
  getVideoJobs: (limit = 20, status = null) => {
    let url = `/videos/jobs?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  
  // PART 10 / Phase E (2026-05-27): Create video from saved content via v2.
  // Legacy `/videos/create-from-content` is permanently 503'd. The v2
  // sibling reuses the SAME content lookup contract (script/prompt/hook/cta
  // from the stored row) and routes through the Content Bot v2 billing gate
  // so extended-tier items must pass `upcharge_confirmed: true` to proceed.
  createVideoFromContent: (contentId, hookOverride = null, ctaOverride = null, opts = {}) =>
    api.post('/videos/create-from-content-v2', {
      content_id: contentId,
      hook_override: hookOverride,
      cta_override: ctaOverride,
      upcharge_confirmed: Boolean(opts.upcharge_confirmed),
      target_duration_sec: opts.target_duration_sec,
    }),

  // Phase E (2026-05-27): Bulk Video Generation routes through v2. The v2
  // batch endpoint validates EVERY item through the billing gate BEFORE
  // any provider call — if any item is extended without upcharge, blocked,
  // or missing, the whole batch is rejected and NO video_jobs rows are
  // created. Hard cap of 10 items per batch.
  createBulkVideos: (contentIds, sourceType = 'content_pack', opts = {}) =>
    api.post('/videos/bulk-create-v2', {
      content_ids: contentIds,
      source_type: sourceType,
      upcharge_confirmed: Boolean(opts.upcharge_confirmed),
      target_duration_sec: opts.target_duration_sec,
    }),
  getBulkVideoStatus: (batchId) => api.get(`/videos/bulk-status-v2/${batchId}`),
  
  // Batch Video Generation API for Weekly Plans
  createBatchJobs: (planId, contentIds = []) => api.post('/video/batch', {
    plan_id: planId,
    content_ids: contentIds
  }),
  getBatchStatus: (planId) => api.get(`/video/batch/${planId}/status`),
  
  // Pay-as-you-go (type-based pricing)
  purchaseExtra: (quantity = 1, videoType = 'broll') => api.post('/purchase-extra-video', { 
    quantity, 
    video_type: videoType 
  }),
  
  // PART 3: Video Credits API
  getCredits: () => api.get('/video-credits/balance'),
  getCreditBundles: () => api.get('/video-credits/bundles'),
  purchaseCredits: (bundleSize) => api.post('/video-credits/purchase', { bundle_size: bundleSize }),
  getCreditHistory: (limit = 20) => api.get(`/video-credits/history?limit=${limit}`),
};

// Billing API
export const billingAPI = {
  getInfo: () => api.get('/billing/info'),
  getPlans: () => api.get('/billing/plans'),
  createCheckout: (plan) => api.post('/create-checkout-session', { plan }),
  createPortal: () => api.post('/create-portal-session'),
};

// Round 218A Phase 8D-2 (2026-02-05) — customer-facing Business
// Credits UI. `getBalance()` powers the ambient header pill and the
// Settings detail section. `purchasePack()` opens a Stripe Checkout
// session for one of the four v3 packs (100/250/500/1000). The
// backend returns 503 when the corresponding STRIPE_PRICE_CREDIT_PACK_*
// env var is blank — the frontend surfaces a Preview-only message in
// that case rather than a broken checkout.
export const businessCreditsAPI = {
  getBalance: () => api.get('/billing/credits'),
  purchasePack: (packKey) => api.post('/billing/credits/purchase-pack', { pack_key: packKey }),
};

// Round 218A Phase 8D-3A (2026-02-05) — Onboarding resume state +
// strategist-foundation prewarm. Every endpoint here is idempotent
// and consumes ZERO Business Credits.
export const onboardingAPI = {
  getState: () => api.get('/onboarding/state').then((r) => r.data),
  saveProgress: (payload) => api.post('/onboarding/progress', payload).then((r) => r.data),
  prewarm: () => api.post('/onboarding/prewarm').then((r) => r.data),
  wowSummary: (refresh = false) =>
    api.get(`/onboarding/wow-summary${refresh ? '?refresh=1' : ''}`).then((r) => r.data),
};

// Journey/Progress API
export const journeyAPI = {
  getStage: () => api.get('/journey/stage'),
};

// AI Intelligence API
export const aiAPI = {
  getWhatNext: (toolUsed = null) => api.get(`/ai/what-next${toolUsed ? `?tool_used=${toolUsed}` : ''}`),
  getGenerationLimit: (toolName) => api.get(`/ai/generation-limit/${toolName}`),
  getFreeLimitPrompt: (toolName = 'content_generation') => api.get(`/ai/free-limit-prompt?tool_name=${toolName}`),
  saveContentMemory: (contentId, contentType, structureSummary = null) => 
    api.post('/ai/content-memory', null, { 
      params: { content_id: contentId, content_type: contentType, structure_summary: structureSummary }
    }),
  getContentMemory: (limit = 10) => api.get(`/ai/content-memory?limit=${limit}`),
  updateBusinessMemory: (data) => api.put('/ai/business-memory', null, { params: data }),
  getBusinessMemory: () => api.get('/ai/business-memory'),
};

// AI Assistant API
export const aiTwinAPI = {
  getSettings: () => api.get('/ai-twin/settings'),
  updateSettings: (settings) => api.put('/ai-twin/settings', settings),
  resetSettings: () => api.delete('/ai-twin/settings'),
};

// Business Brain Results API
export const businessResultsAPI = {
  getWeeklySnapshot: () => api.get('/business-brain/results/weekly-snapshot'),
  getRevenue: () => api.get('/business-brain/results/revenue'),
  addRevenue: (amount, note = null) => api.post('/business-brain/results/revenue', { amount, note }),
  getNextAction: () => api.get('/business-brain/results/next-action'),
  getMomentum: () => api.get('/business-brain/momentum'),
};

// Creations API - Unified storage for all business assets
export const creationsAPI = {
  save: (type, title, content) => api.post('/creations/save', { type, title, content }),
  getAll: (type = null, limit = 50, includeLegacy = false) => {
    let url = `/creations/all?limit=${limit}`;
    if (type) url += `&type=${type}`;
    if (includeLegacy) url += `&include_legacy=true`;
    return api.get(url);
  },
  getById: (id) => api.get(`/creations/${id}`),
  // Round 58 — reopen the FULL saved asset (content + PDF + HTML + URLs).
  getFull: (id, refreshPdf = true) =>
    api.get(`/creations/${id}/full?refresh_pdf=${refreshPdf ? 'true' : 'false'}`),
  delete: (id) => api.delete(`/creations/${id}`),
  generatePDF: (type, data) => api.post('/creations/generate-pdf', { type, data }),
  getStats: () => api.get('/creations/stats/summary'),
};


// Weekly Planner = Metricool Scheduling API (2026-05-29).
// Asset scheduler — never generates video. Founder-only on the backend.
export const weeklyPlannerAPI = {
  importFromRMB: (postIds, targetWeekStart = null) =>
    api.post('/weekly-planner/items/import-from-rmb', {
      post_ids: postIds,
      target_week_start: targetWeekStart,
    }).then((r) => r.data),
  attachVideo: (itemId, contentId) =>
    api.post('/weekly-planner/items/attach-video', {
      item_id: itemId,
      content_id: contentId,
    }).then((r) => r.data),
  attachImage: (itemId, assetUrl, assetId = null) =>
    api.post('/weekly-planner/items/attach-image', {
      item_id: itemId,
      asset_url: assetUrl,
      asset_id: assetId,
    }).then((r) => r.data),
  listItems: (filters = {}) => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return api.get(`/weekly-planner/items${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  listAvailableVideos: () =>
    api.get('/weekly-planner/available-videos').then((r) => r.data),
  patch: (itemId, updates) =>
    api.patch(`/weekly-planner/items/${itemId}`, updates).then((r) => r.data),
  delete: (itemId) =>
    api.delete(`/weekly-planner/items/${itemId}`).then((r) => r.data),
  sendToMetricool: (itemId) =>
    api.post(`/weekly-planner/items/${itemId}/send-to-metricool`).then((r) => r.data),
};


// Import Existing Video (2026-05-29). Founder-only multipart upload.
// NEVER triggers Runway / ElevenLabs / video generation. Backend
// runs ffprobe before persisting the row.
export const videoImportAPI = {
  getLimits: () => api.get('/videos/import/limits').then((r) => r.data),
  importFile: (file, title = '', onUploadProgress = undefined) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return api.post('/videos/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }).then((r) => r.data);
  },
};

// Round 113 — Brand Brain (persistent visual identity memory).
// Every builder should retrieve branding from this API; users
// should never be asked to re-enter branding.
export const brandBrainAPI = {
  get: () => api.get('/brand-brain').then((r) => r.data),
  patch: (fields) => api.patch('/brand-brain', fields).then((r) => r.data),
  completeOnboarding: () =>
    api.post('/brand-brain/onboarding/complete').then((r) => r.data),
  uploadLogo: (file, kind = 'primary') => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return api
      .post('/brand-brain/upload-logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  uploadGuidelines: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post('/brand-brain/upload-guidelines', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  analyzeLogo: (file, save = true) => {
    const form = new FormData();
    form.append('file', file);
    form.append('save', save ? 'true' : 'false');
    return api
      .post('/brand-brain/analyze-logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  analyzeWebsite: (url, save = true) =>
    api
      .post(`/brand-brain/analyze-website?save=${save ? 'true' : 'false'}`, { url })
      .then((r) => r.data),
  generateFromQuestionnaire: (answers, save = true) =>
    api
      .post(
        `/brand-brain/generate-from-questionnaire?save=${save ? 'true' : 'false'}`,
        answers,
      )
      .then((r) => r.data),
  changelog: (limit = 50) =>
    api.get(`/brand-brain/changelog?limit=${limit}`).then((r) => r.data),
  // Round 114 (Phase C) — Logo suite. Round 218E P0 (2026-02-07):
  // ``generateLogos`` now runs on a background job. We poll every
  // 2s until status != running (typical p95 ~ 45-60s for 3 images).
  // On success it resolves with the same {brand_brain, concepts}
  // shape callers already expected.
  generateLogos: async (count = 3, excludeDirections = []) => {
    const start = await api
      .post('/brand-brain/logos/generate', {
        count,
        exclude_directions: excludeDirections,
      })
      .then((r) => r.data);
    if (!start?.job_id) return start; // legacy sync fallback
    const jobId = start.job_id;
    // Poll for up to 3 minutes. GPT Image 1 typically finishes in
    // < 90s even for 3 concepts.
    const deadline = Date.now() + 180_000;
    while (true) {
      await new Promise((res) => setTimeout(res, 2000));
      const status = await api
        .get(`/brand-brain/logos/generate/status/${jobId}`)
        .then((r) => r.data);
      if (status.status === 'success') {
        return {
          concepts: (status.brand_brain?.logo_urls || [])
            .filter((l) => l.kind === 'concept'),
          brand_brain: status.brand_brain,
        };
      }
      if (status.status === 'failed') {
        const msg = status.error || 'logo generation failed';
        throw new Error(msg);
      }
      if (Date.now() > deadline) {
        throw new Error('logo generation timed out after 3 minutes');
      }
    }
  },
  selectPrimary: (conceptId) =>
    api
      .post('/brand-brain/logos/select-primary', { concept_id: conceptId })
      .then((r) => r.data),
  deleteLogoEntry: (entryId) =>
    api.delete(`/brand-brain/logos/${entryId}`).then((r) => r.data),
  deleteConcept: (conceptId) =>
    api.delete(`/brand-brain/logos/concept/${conceptId}`).then((r) => r.data),
  toggleFavorite: (entryId) =>
    api.post(`/brand-brain/logos/${entryId}/favorite`).then((r) => r.data),
  regenerateGuidelinesPdf: () =>
    api.post('/brand-brain/guidelines-pdf').then((r) => r.data),
};

// Round 115 — Sales Page Builder (Universal Builder flagship).
// Every consumer of this API automatically inherits Business Brain,
// Brand Brain, Market Intelligence and approved-asset reuse.
export const salesPageAPI = {
  context: () => api.get('/sales-page/context').then((r) => r.data),
  current: () => api.get('/sales-page/current').then((r) => r.data),
  generate: () => api.post('/sales-page/generate').then((r) => r.data),
  regenerateSection: (section) =>
    api.post('/sales-page/regenerate-section', { section }).then((r) => r.data),
  patch: (payload) => api.patch('/sales-page', payload).then((r) => r.data),
};

// Round 116 (Phase E2) — Universal Content Builder + Universal
// Lead Magnet Builder. Both routes auto-load Business Brain +
// Brand Brain + Market Intelligence + approved-asset reuse via
// the shared Universal Builder Foundation. Every new builder
// should hit endpoints in this shape — no bespoke context loading.
export const universalContentAPI = {
  generate: (topic = '', contentType = 'post') =>
    api
      .post('/content/universal-generate', { topic, content_type: contentType })
      .then((r) => r.data),
};

export const universalLeadMagnetAPI = {
  generate: (leadMagnetType = '', businessDescription = '') =>
    api
      .post('/lead-magnet/universal-generate', {
        lead_magnet_type: leadMagnetType,
        business_description: businessDescription,
      })
      .then((r) => r.data),
};

// Round 117 (Phase E3) — Universal Builder dispatch. One endpoint
// serves every registered builder (email, ebook, video, business
// plan, business card, presentation, flyer, brochure, ad, social
// media, packaging, product label, merchandise, proposal, invoice).
//
// Round 218A Phase 8D-2 (2026-02-05) — the generate call carries the
// customer's `attempt_id` + optional `asset_id` so the Phase 8D-1
// credit gate can deduplicate double-clicks and network retries.
// Callers pass `mode: "generate"` for the real production_ready →
// generated transition; omit or pass `"draft"` for free preview
// generation.
export const universalBuilderAPI = {
  list: () => api.get('/universal-builder/list').then((r) => r.data),
  context: (builderType) =>
    api.get(`/universal-builder/${builderType}/context`).then((r) => r.data),
  generate: (builderType, opts = {}) => {
    const {
      topic = '',
      extras = null,
      mode = 'draft',
      attemptId = null,
      assetId = null,
      assetKind = null,
    } = typeof opts === 'string' ? { topic: opts } : opts;
    return api
      .post(`/universal-builder/${builderType}/generate`, {
        topic,
        extras,
        mode,
        attempt_id: attemptId,
        asset_id: assetId,
        asset_kind: assetKind,
      })
      .then((r) => r.data);
  },
};

// Round 118 (Phase F1) — Business Operating System. The customer's
// digital business headquarters: completion %s, AI recommendations,
// versioned downloadable snapshots.
export const businessOsAPI = {
  dashboard: () => api.get('/business-os/dashboard').then((r) => r.data),
  createSnapshot: () => api.post('/business-os/snapshot').then((r) => r.data),
  snapshots: () => api.get('/business-os/snapshots').then((r) => r.data),
  getSnapshot: (version) =>
    api.get(`/business-os/snapshots/${version}`).then((r) => r.data),
  // Round 119 (Phase F2) — Share & Collaboration
  createShare: (payload) =>
    api.post('/business-os/shares', payload).then((r) => r.data),
  listShares: () => api.get('/business-os/shares').then((r) => r.data),
  revokeShare: (shareId) =>
    api.post(`/business-os/shares/${shareId}/revoke`).then((r) => r.data),
  audit: (shareId) =>
    api.get(`/business-os/shares/${shareId}/audit`).then((r) => r.data),
  // Round 203 (Phase G) — Snapshot Diff (AI-generated change summary)
  snapshotDiff: (version, fromVersion) =>
    api.get(`/business-os/snapshots/${version}/diff`, {
      params: fromVersion ? { from_version: fromVersion } : {},
    }).then((r) => r.data),
  regenerateDiff: (version, fromVersion) =>
    api.post(`/business-os/snapshots/${version}/diff/regenerate`, null, {
      params: fromVersion ? { from_version: fromVersion } : {},
    }).then((r) => r.data),
  // Round 203 (Phase G) — Collaboration inbox (owner-side)
  ownerInbox: () => api.get('/business-os/comments').then((r) => r.data),
  shareComments: (shareId) =>
    api.get(`/business-os/shares/${shareId}/comments`).then((r) => r.data),
  ownerReply: (shareId, payload) =>
    api.post(`/business-os/shares/${shareId}/comments`, payload).then((r) => r.data),
  ownerResolve: (shareId, commentId) =>
    api.post(`/business-os/shares/${shareId}/comments/${commentId}/resolve`).then((r) => r.data),
  // Round 203 (Phase G) — Resend email delivery
  resendEmail: (shareId, token) =>
    api.post(`/business-os/shares/${shareId}/resend-email`, { token }).then((r) => r.data),
};

// Public portal API (unauthenticated but token-gated).
// Uses raw axios (defined at file top) to bypass the JWT interceptor.
const portalAxios = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
export const businessOsPortalAPI = {
  landing: (token) =>
    portalAxios.get(`/business-os/portal/${token}`).then((r) => r.data),
  diff: (token, fromVersion) =>
    portalAxios.get(`/business-os/portal/${token}/diff`, {
      params: fromVersion ? { from_version: fromVersion } : {},
    }).then((r) => r.data),
  listComments: (token, target_type, target_id) =>
    portalAxios.get(`/business-os/portal/${token}/comments`, {
      params: { target_type, target_id },
    }).then((r) => r.data),
  createComment: (token, payload) =>
    portalAxios.post(`/business-os/portal/${token}/comments`, payload).then((r) => r.data),
  resolveComment: (token, commentId, payload) =>
    portalAxios.post(`/business-os/portal/${token}/comments/${commentId}/resolve`, payload).then((r) => r.data),
};

